import { useRef, useState, useCallback } from 'react';

/**
 * useCalendarDnD (T-cal21) — 시간격자(주/일 뷰) 이벤트 블록 드래그 이동.
 *
 * Pointer Events 기반(마우스/터치 통합). 본 1차 버전은 **이동(move)** 만:
 *   - pointerdown(블록) → 드래그 시작, 시작 시각 캡처.
 *   - pointermove → Y 픽셀 delta → 분 단위(스냅 15분) 변환 → 미리보기 offset.
 *   - pointerup → onMove(eventId, deltaMinutes) 호출(부모가 PATCH).
 * 생성/리사이즈는 후속(T-cal21 2~4분할). reduced-motion 시 미리보기 transform 생략.
 *
 * 정밀도: `HOUR_PX` 기준. **2026-09-29 사용자 확정으로 48 → 60** 이 되어 **1분 = 1px** 이다
 * (종전 48 일 때는 1분 = 0.8px 이었다). 스냅은 그대로 15분.
 *
 * ⭐ **옆 날짜로 넘기기** (2026-10-13 · 옛 가지 b0e0ab630 의 주간 갈래만 이식).
 *   `getDayColAt(clientX, clientY)` 를 주입하면 pointermove 마다 «지금 커서 아래의 날짜 열»을 다시 읽어
 *   시작 열과의 차(`dayDelta`)를 낸다. 놓으면 `onMove(key, deltaMinutes, dayDelta)` — 부모가 하루=1440분으로
 *   더한다. ⚠ setPointerCapture 뒤에는 `target` 이 늘 잡은 요소라, 좌표 기반 hit-test(elementFromPoint)를
 *   훅 «밖»에서 주입받는다(이 훅은 DOM 구조를 몰라도 된다).
 *   ⚠ 미주입이면 dayDelta 는 늘 0 — 종전과 100% 같다(세 번째 인자는 받는 쪽이 무시해도 된다).
 *
 * ⛔ **그 값을 여기에 다시 적지 않는다.** 종전에는 이 파일과 `TimeGridView.tsx` 가 48 을 각자
 * 적어 두어, 한쪽만 고치면 **끌어 옮긴 블록이 엉뚱한 시각에 놓였다**(60 으로 잰 분을 48 로
 * 그리면 1.25배 어긋난다). 한 곳(`timeGridMetrics.ts`)에서 가져온다.
 */
import { HOUR_PX } from './timeGridMetrics';

const SNAP_MIN = 15;

/** px Y delta → 스냅된 분(15분 단위). pure — 테스트 가능. */
export function pxToSnappedMinutes(dyPx: number, hourPx = HOUR_PX, snapMin = SNAP_MIN): number {
  const rawMin = (dyPx / hourPx) * 60;
  return Math.round(rawMin / snapMin) * snapMin;
}

export interface DragState {
  eventKey: string;       // occurrenceKey
  deltaMinutes: number;   // 스냅된 이동 분
  dayDelta?: number;      // 옆으로 넘어간 날짜 열 수(0=그대로). getDayColAt 미주입이면 늘 0.
}

interface UseCalendarDnDArgs {
  // 드래그 종료 시 호출 — 부모가 PATCH. occurrenceKey 와 분 delta.
  onMove: (eventKey: string, deltaMinutes: number, dayDelta: number) => void;
  /** 좌표 → 날짜 열 index. 미주입이면 dayDelta 는 늘 0(종전 동작 그대로). */
  getDayColAt?: (clientX: number, clientY: number) => number | null;
}

export function useCalendarDnD({ onMove, getDayColAt }: UseCalendarDnDArgs) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const startY = useRef(0);
  const startKey = useRef<string>('');
  const startDayCol = useRef<number | null>(null);
  const movedRef = useRef(false);

  const onPointerDown = useCallback((e: React.PointerEvent, eventKey: string) => {
    // 좌클릭/터치만. 우클릭·보조버튼 제외.
    if (e.button !== 0) return;
    startY.current = e.clientY;
    startKey.current = eventKey;
    startDayCol.current = getDayColAt ? getDayColAt(e.clientX, e.clientY) : null;
    movedRef.current = false;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDrag({ eventKey, deltaMinutes: 0, dayDelta: 0 });
  }, [getDayColAt]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!startKey.current) return;
    const dyPx = e.clientY - startY.current;
    const snapped = pxToSnappedMinutes(dyPx);
    if (Math.abs(dyPx) > 3) movedRef.current = true;
    let dayDelta = 0;
    if (getDayColAt && startDayCol.current != null) {
      const cur = getDayColAt(e.clientX, e.clientY);
      // 열 밖(눈금·머리 등)이면 null — 그때는 «직전 열»을 지킨다(0 으로 튀지 않게).
      if (cur == null) {
        setDrag((d) => (d ? { ...d, deltaMinutes: snapped } : d));
        return;
      }
      dayDelta = cur - startDayCol.current;
      if (dayDelta !== 0) movedRef.current = true;
    }
    setDrag((d) => (d ? { ...d, deltaMinutes: snapped, dayDelta } : d));
  }, [getDayColAt]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    const key = startKey.current;
    if (!key) return;
    try { (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* */ }
    const moved = movedRef.current;
    const delta = drag?.deltaMinutes ?? 0;
    const dayDelta = drag?.dayDelta ?? 0;
    startKey.current = '';
    startDayCol.current = null;
    setDrag(null);
    // 실제로 이동(시각 스냅 또는 날짜 열 중 하나라도 0 아님)했고 클릭 임계 넘었을 때만 PATCH.
    if (moved && (delta !== 0 || dayDelta !== 0)) onMove(key, delta, dayDelta);
  }, [drag, onMove]);

  return {
    drag,
    isDragging: drag != null,
    /** 드래그 중인 블록인지 + 미리보기 px offset(transform용). */
    previewOffsetPx: (eventKey: string): number =>
      drag && drag.eventKey === eventKey ? (drag.deltaMinutes / 60) * HOUR_PX : 0,
    /** 끌고 있는 블록이 옆으로 넘어간 날짜 열 수(X 미리보기 — px 환산은 호출측이 열 폭으로). */
    previewDayDelta: (eventKey: string): number =>
      drag && drag.eventKey === eventKey ? (drag.dayDelta ?? 0) : 0,
    handlers: { onPointerDown, onPointerMove, onPointerUp },
    /** 클릭 vs 드래그 구분 — 드래그였으면 onClick 무시용. */
    wasMoved: () => movedRef.current,
  };
}
